import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { subscribeAllEntries } from '@/data/logEntries'
import { subscribeFavorites, subscribeRecents } from '@/data/favorites'
import { updateStreak } from '@/data/streak'
import { todayStr, addDays } from '@/lib/dates'
import { aggregateMacros, type DayTotals } from '@/lib/macros'
import type { LogEntry, FoodEntry, ExerciseEntry, WaterEntry, NoteEntry, QuickFoodItem } from '@/types/entries'

interface LogContextValue {
  date: string
  setDate: (d: string) => void
  goToday: () => void
  prevDay: () => void
  nextDay: () => void
  allEntries: LogEntry[]
  loading: boolean
  /** Food / mood / symptom / med entries for the selected date, by time. */
  dayEntries: LogEntry[]
  foodEntries: FoodEntry[]
  exerciseEntries: ExerciseEntry[]
  totalBurned: number
  waterEntry: WaterEntry | null
  noteEntry: NoteEntry | null
  daysWithFood: Set<string>
  totals: DayTotals
  hasLoggedFoodToday: boolean
  favorites: Record<string, QuickFoodItem>
  recents: Record<string, QuickFoodItem>
  isFavorite: (foodId: string) => boolean
}

const LogContext = createContext<LogContextValue | null>(null)
const DATE_KEY = 'vore_log_date'

export function LogProvider({ children }: { children: ReactNode }) {
  const { uid, userSettings } = useAuth()
  const [date, setDateState] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem(DATE_KEY)
      return saved && /^\d{4}-\d{2}-\d{2}$/.test(saved) ? saved : todayStr()
    } catch {
      return todayStr()
    }
  })
  const [allEntries, setAllEntries] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [favorites, setFavorites] = useState<Record<string, QuickFoodItem>>({})
  const [recents, setRecents] = useState<Record<string, QuickFoodItem>>({})

  const setDate = useCallback((d: string) => {
    setDateState(d)
    try {
      sessionStorage.setItem(DATE_KEY, d)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (!uid) return
    setLoading(true)
    const unsub = subscribeAllEntries(
      uid,
      (entries) => {
        setAllEntries(entries)
        setLoading(false)
      },
      () => setLoading(false),
    )
    const unsubFav = subscribeFavorites(uid, setFavorites)
    const unsubRecent = subscribeRecents(uid, setRecents)
    // Startup streak continuity check (mobile LogScreen.initializeApp)
    void updateStreak(uid, null)
    return () => {
      unsub()
      unsubFav()
      unsubRecent()
    }
  }, [uid])

  // Jump back to today when the tab wakes up on a new day (mobile AppState listener).
  useEffect(() => {
    let lastActive = todayStr()
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const today = todayStr()
      if (lastActive !== today) {
        setDate(today)
        lastActive = today
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [setDate])

  const value = useMemo<LogContextValue>(() => {
    const byTime = (a: LogEntry, b: LogEntry) => new Date(a.time).getTime() - new Date(b.time).getTime()
    const forDate = allEntries.filter((e) => e.date === date)
    const dayEntries = forDate.filter((e) => e.cat !== 4 && e.cat !== 5 && e.cat !== 6).sort(byTime)
    const foodEntries = dayEntries.filter((e) => e.cat === 0) as FoodEntry[]
    const exerciseEntries = forDate.filter((e) => e.cat === 4).sort(byTime) as ExerciseEntry[]
    const totalBurned = exerciseEntries.reduce((acc, e) => acc + (Number(e.value) || 0), 0)
    const waterEntry = (forDate.find((e) => e.cat === 5) as WaterEntry | undefined) || null
    const noteEntry = (forDate.find((e) => e.cat === 6) as NoteEntry | undefined) || null
    const daysWithFood = new Set<string>()
    const today = todayStr()
    let hasLoggedFoodToday = false
    for (const e of allEntries) {
      if (e.cat === 0) {
        daysWithFood.add(e.date)
        if (e.date === today) hasLoggedFoodToday = true
      }
    }
    const totals = aggregateMacros(dayEntries, userSettings?.profile, totalBurned)
    return {
      date,
      setDate,
      goToday: () => setDate(todayStr()),
      prevDay: () => setDate(addDays(date, -1)),
      nextDay: () => setDate(addDays(date, 1)),
      allEntries,
      loading,
      dayEntries,
      foodEntries,
      exerciseEntries,
      totalBurned,
      waterEntry,
      noteEntry,
      daysWithFood,
      totals,
      hasLoggedFoodToday,
      favorites,
      recents,
      isFavorite: (id) => !!favorites[id],
    }
  }, [allEntries, date, loading, favorites, recents, userSettings?.profile, setDate])

  return <LogContext.Provider value={value}>{children}</LogContext.Provider>
}

export function useLog(): LogContextValue {
  const ctx = useContext(LogContext)
  if (!ctx) throw new Error('useLog must be used within LogProvider')
  return ctx
}
